import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Box, TextField, Select, MenuItem, FormControl, InputLabel,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Chip, Snackbar, Alert,
    DialogContentText, Pagination, CircularProgress
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';

const PurchaseOrders = () => {
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [availableLocations, setAvailableLocations] = useState([]);
    const [userLocation, setUserLocation] = useState('');
    const [userRole, setUserRole] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [isLoading, setIsLoading] = useState(true);

    // Filters and pagination
    const [productSearch, setProductSearch] = useState('');
    const [location, setLocation] = useState('All');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [entriesPerPage, setEntriesPerPage] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({
        current: 1,
        total: 1,
        hasNext: false,
        hasPrev: false,
        totalRecords: 0,
        limit: 20
    });

    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }

        const storedLocation = localStorage.getItem('userLocation');
        const role = localStorage.getItem('userRole');
        setUserLocation(storedLocation);
        setUserRole(role);

        fetchLocations();
    }, [navigate]);

    const fetchLocations = async () => {
        try {
            const locationsResponse = await api.get('/config/locations');
            setAvailableLocations(locationsResponse.data.storageLocations);
        } catch (error) {
            console.error('Error fetching locations:', error);
        }
    };

    // Fetch purchase orders with pagination and filters
    const fetchPurchaseOrders = useCallback(async (page = 1) => {
        setIsLoading(true);
        try {
            const params = {
                page,
                limit: entriesPerPage
            };

            // Add filters only if they have values
            if (productSearch && productSearch.trim()) {
                params.productSearch = productSearch.trim();
            }
            if (location && location !== 'All') {
                params.location = location;
            }
            if (startDate) {
                params.startDate = startDate;
            }
            if (endDate) {
                params.endDate = endDate;
            }

            const res = await api.get('/purchase-orders/paginated', { params });
            setPurchaseOrders(res.data.purchaseOrders);
            setPagination(res.data.pagination);
            setCurrentPage(page);
        } catch (error) {
            console.error('Error fetching purchase orders:', error);
            setSnackbar({
                open: true,
                message: 'Failed to fetch purchase orders',
                severity: 'error'
            });
        } finally {
            setIsLoading(false);
        }
    }, [entriesPerPage, productSearch, location, startDate, endDate]);

    // Initial load and when entriesPerPage changes
    useEffect(() => {
        if (userRole) {
            fetchPurchaseOrders(1);
        }
        // fetchPurchaseOrders is defined below - intentionally excluded to avoid re-fetches
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entriesPerPage, userRole]);

    const handlePageChange = (event, value) => {
        fetchPurchaseOrders(value);
    };

    const handleSearch = () => {
        fetchPurchaseOrders(1); // Reset to page 1 when searching
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handleRowClick = (order) => {
        setSelectedOrder(order);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedOrder(null);
    };

    const handleDeleteClick = () => {
        setConfirmDeleteOpen(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await api.delete(`/purchase-orders/${selectedOrder._id}`, {
                data: { confirmed: true }
            });

            setSnackbar({
                open: true,
                message: 'Purchase order deleted successfully. Stock has been updated.',
                severity: 'success'
            });

            setConfirmDeleteOpen(false);
            setDialogOpen(false);
            setSelectedOrder(null);

            // Refresh the current page
            fetchPurchaseOrders(currentPage);
        } catch (error) {
            console.error('Error deleting purchase order:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to delete purchase order',
                severity: 'error'
            });
        }
    };

    const handleCancelDelete = () => {
        setConfirmDeleteOpen(false);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const getTotalQuantity = (products) => {
        return products.reduce((sum, p) => sum + p.quantity, 0).toFixed(2);
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}
                >
                    Purchase Orders
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Track and manage all incoming inventory purchases
                </Typography>
                <Button
                    variant="contained"
                    color="success"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/create-purchase-order')}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                    Create Purchase Order
                </Button>
            </Box>

            {/* Filters Section */}
            <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Search by Product Name"
                            value={productSearch}
                            onChange={(e) => setProductSearch(e.target.value)}
                            onKeyPress={handleKeyPress}
                            variant="outlined"
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={6} sm={4} md={2}>
                        <TextField
                            fullWidth
                            label="Start Date"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={6} sm={4} md={2}>
                        <TextField
                            fullWidth
                            label="End Date"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            size="small"
                        />
                    </Grid>

                    {(userRole === 'owner' || userLocation === 'Universal') && (
                        <Grid item xs={6} sm={4} md={2}>
                            <FormControl fullWidth variant="outlined" size="small">
                                <InputLabel>Location</InputLabel>
                                <Select
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                    label="Location"
                                >
                                    <MenuItem value="All">All</MenuItem>
                                    {availableLocations.map((loc) => (
                                        <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}

                    <Grid item xs={6} sm={4} md={userRole === 'owner' || userLocation === 'Universal' ? 2 : 4}>
                        <FormControl fullWidth variant="outlined">
                            <TextField
                                select
                                label="Entries per page"
                                value={entriesPerPage}
                                onChange={(e) => setEntriesPerPage(e.target.value)}
                                variant="outlined"
                                size="small"
                            >
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={20}>20</MenuItem>
                                <MenuItem value={30}>30</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                            </TextField>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={userRole === 'owner' || userLocation === 'Universal' ? 3 : 4}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            startIcon={<SearchIcon />}
                            onClick={handleSearch}
                            sx={{ height: '40px', textTransform: 'none' }}
                        >
                            Search
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'success.main' }}>
                                    <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Location</Typography></TableCell>
                                    <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Purchase Date</Typography></TableCell>
                                    <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Products</Typography></TableCell>
                                    <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Total Quantity</Typography></TableCell>
                                    <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Created By</Typography></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {purchaseOrders.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body1" color="text.secondary">
                                                No purchase orders found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    purchaseOrders.map((order) => (
                                        <TableRow
                                            key={order._id}
                                            hover
                                            onClick={() => handleRowClick(order)}
                                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                                        >
                                            <TableCell>
                                                <Chip label={order.location} size="small" color="primary" />
                                            </TableCell>
                                            <TableCell>{formatDate(order.purchaseDate)}</TableCell>
                                            <TableCell>{order.products.length}</TableCell>
                                            <TableCell>{getTotalQuantity(order.products)}</TableCell>
                                            <TableCell>{order.createdBy?.name || 'Unknown'}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    {pagination.total > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Pagination
                                count={pagination.total}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                size="large"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}

                    {/* Summary Info */}
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Showing {purchaseOrders.length > 0 ? ((currentPage - 1) * entriesPerPage + 1) : 0} to {Math.min(currentPage * entriesPerPage, pagination.totalRecords)} of {pagination.totalRecords} purchase orders
                        </Typography>
                    </Box>
                </>
            )}

            {/* View Details Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                {selectedOrder && (
                    <>
                        <DialogTitle sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                            Purchase Order Details
                        </DialogTitle>
                        <DialogContent sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Location</Typography>
                                    <Typography variant="body1" fontWeight="600">{selectedOrder.location}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Purchase Date</Typography>
                                    <Typography variant="body1" fontWeight="600">{formatDate(selectedOrder.purchaseDate)}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Created By</Typography>
                                    <Typography variant="body1" fontWeight="600">{selectedOrder.createdBy?.name || 'Unknown'}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Created At</Typography>
                                    <Typography variant="body1" fontWeight="600">{formatDate(selectedOrder.createdAt)}</Typography>
                                </Grid>
                                {selectedOrder.notes && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">Notes</Typography>
                                        <Typography variant="body1">{selectedOrder.notes}</Typography>
                                    </Grid>
                                )}
                            </Grid>

                            <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
                                Products
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                                            <TableCell><Typography variant="subtitle2" fontWeight="bold">Product</Typography></TableCell>
                                            <TableCell align="right"><Typography variant="subtitle2" fontWeight="bold">Quantity</Typography></TableCell>
                                            <TableCell align="right"><Typography variant="subtitle2" fontWeight="bold">Rate</Typography></TableCell>
                                            <TableCell align="right"><Typography variant="subtitle2" fontWeight="bold">Total</Typography></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {selectedOrder.products.map((product, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{product.productName}</TableCell>
                                                <TableCell align="right">{product.quantity}</TableCell>
                                                <TableCell align="right">₹{product.rate ? product.rate.toFixed(2) : '0.00'}</TableCell>
                                                <TableCell align="right">
                                                    ₹{product.rate ? (product.quantity * product.rate).toFixed(2) : '0.00'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                                            <TableCell colSpan={3} align="right"><Typography fontWeight="bold">Grand Total:</Typography></TableCell>
                                            <TableCell align="right">
                                                <Typography fontWeight="bold">
                                                    ₹{selectedOrder.products.reduce((sum, p) => sum + (p.quantity * (p.rate || 0)), 0).toFixed(2)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </DialogContent>
                        <DialogActions>
                            {(userRole === 'owner' || userRole === 'employee') && (
                                <Button
                                    onClick={handleDeleteClick}
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Delete Order
                                </Button>
                            )}
                            <Button onClick={handleCloseDialog} sx={{ textTransform: 'none' }}>Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={confirmDeleteOpen}
                onClose={handleCancelDelete}
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this purchase order? This will reduce the stock quantities for all products in this order. This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained" sx={{ textTransform: 'none' }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default PurchaseOrders;
